import { Component, DoCheck, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, merge, Observable, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ApiKey, ApiKeysService, ApiProvider } from '../../services/api-keys.service';
import { ApiKeyCreateDialogComponent } from '../apikeys/apikey-create-dialog.component';
import { ApiKeyPreview } from '../apikeys/api-key-editor.component';
import { NgModel } from '@angular/forms';
import { ConversationVoiceService } from '../../services/conversation-voice.service';
import { ConfirmationDialogService } from '../../shared/ui/confirmation-dialog/confirmation-dialog.service';

type DeviceKind = 'speaker' | 'microphone';
interface DeviceOption { id: string; label: string; }
interface LiveProvider {
  name: string;
  /** API key provider the Live product uses; its Voice key rows are this product's configuration. */
  vendor: string;
  available: boolean;
  description: string;
  pricingUrl: string;
  docsUrl: string;
  models: string[];
  model: string;
  expanded: boolean;
}
interface SharedKeyPreview { id: string; provider: string; name: string; secretMasked: string; credential?: ApiKey; }
interface ApiGroupPreview {
  label: string;
  description: string;
  providers: ApiProvider[];
  keys: (ApiKeyPreview & { primary: boolean; credentialId: string })[];
  expanded: boolean;
  provider: string;
  credentialId: string;
  model: string;
}
interface OutputPicker extends MediaDevices { selectAudioOutput?: () => Promise<MediaDeviceInfo>; }

const listError = 'Could not list devices. Retry microphone test.';

@Component({
  selector: 'app-voice-settings',
  templateUrl: './voice-settings.component.html',
  styleUrls: ['../configuration-list.css', '../service-panels.css', '../apikeys/api-key-list.component.css', './voice-settings.component.css'],
})
export class VoiceSettingsComponent implements OnInit, DoCheck, OnDestroy {
  readonly devices: { kind: DeviceKind; label: string; options: DeviceOption[] }[] = [
    { kind: 'speaker', label: 'Speaker', options: [{ id: '', label: 'System default' }] },
    { kind: 'microphone', label: 'Microphone', options: [{ id: '', label: 'System default' }] },
  ];
  expanded = false;
  method: 'live' | 'api' = 'live';
  // Only GPT-Live is implemented. Future providers have no credential or connection actions.
  readonly liveProviders: LiveProvider[] = [
    { name: 'GPT-Live', vendor: 'openai', description: 'Talk with GPT-Live using your OpenAI API key.',
      pricingUrl: 'https://developers.openai.com/api/docs/pricing',
      docsUrl: 'https://developers.openai.com/api/docs/guides/live',
      // Implemented: models come from the backend provider catalog.
      models: [] },
    { name: 'Gemini Live', vendor: 'google', description: 'Talk with Gemini using your Google AI API key.',
      pricingUrl: 'https://ai.google.dev/gemini-api/docs/pricing',
      docsUrl: 'https://ai.google.dev/gemini-api/docs/live-api',
      models: ['gemini-3.8-live', 'gemini-3.8-live-extended-thinking'] },
    { name: 'Grok Voice', vendor: 'xai', description: 'Talk with Grok using your xAI API key.',
      pricingUrl: 'https://docs.x.ai/developers/pricing',
      docsUrl: 'https://docs.x.ai/developers/model-capabilities/audio/speech-to-speech',
      models: ['grok-voice-latest'] },
    { name: 'Nova 2 Sonic', vendor: 'aws', description: 'Use Amazon Nova for live voice conversations through Bedrock.',
      pricingUrl: 'https://aws.amazon.com/bedrock/pricing/',
      docsUrl: 'https://docs.aws.amazon.com/nova/latest/nova2-userguide/sonic-getting-started.html',
      models: ['amazon.nova-2-sonic-v1:0'] },
  ].map((provider, index) => ({ ...provider, available: index === 0, expanded: false, model: '' }));
  readonly apiKeyPreviews: ApiGroupPreview[] = [
    { label: 'Speech', description: 'Convert text into spoken audio.' },
    { label: 'Transcribe', description: 'Convert speech into text.' },
  ].map(group => ({ ...group, keys: [], expanded: false, provider: 'openai', credentialId: '', model: '', providers: ['OpenAI', 'Google', 'xAI'].map(label => ({
    id: label.toLowerCase(), label, service: group.label.toLowerCase(), needsModel: true,
    secretPlaceholder: 'API key', modelPlaceholder: 'Model name',
  })) }));
  // Review-only metadata. Draft secrets are discarded by the shared key editor.
  readonly sharedKeyPreviews: SharedKeyPreview[] = [];
  selectedLiveKeyId = '';
  private nextPreviewKeyId = 1;
  liveCredential?: ApiKey;
  /** Company keys of every service (tenant scope); Voice rows are the saved Live configuration. */
  companyKeys: ApiKey[] = [];
  /** Each Live provider's speaking voices from the backend catalog (first is its default), by API key provider. */
  private providerVoices: Record<string, string[]> = {};
  liveLoading = false;
  liveAvailable = false;
  liveError = '';
  /** A company-key mutation workflow (Connect / Disconnect confirm+write / Primary) is in flight;
   *  blocks a second one — including repeated Disconnect clicks opening several dialogs. */
  liveWriting = false;
  private liveVersion = 0;
  private readonly destroyed$ = new Subject<void>();
  private keyDialog?: MatDialogRef<ApiKeyCreateDialogComponent>;
  readonly segments = Array.from({ length: 10 }, (_, index) => index);
  errors: Record<DeviceKind, string> = { speaker: '', microphone: '' };
  /**
   * The latest enumeration listed labelled devices of this kind. Even then a saved ID absent from
   * the list is only "not listed": enumeration cannot tell an unplugged device from a hidden one.
   */
  verified: Record<DeviceKind, boolean> = { speaker: false, microphone: false };
  // Local test evidence, never inferred from enumeration or persisted across panel lifetimes.
  // A passed test is remembered in this browser for the device it tested; another device needs a new test.
  private isTested(kind: DeviceKind): boolean { return this.voice.settings.testedDevices[kind] === this.selected(kind); }
  private setTested(kind: DeviceKind, passed: boolean): void {
    const tested = { ...this.voice.settings.testedDevices };
    if (passed) tested[kind] = this.selected(kind); else delete tested[kind];
    this.voice.saveSettings({ testedDevices: tested });
  }
  private clearTested(): void { this.voice.saveSettings({ testedDevices: {} }); }
  testing: DeviceKind | null = null;
  inputLevel = 0;
  private testAbort?: AbortController;
  private destroyed = false;
  private refreshVersion = 0;
  /** Saved IDs this panel last rendered, so a change from another panel or account resets state about the old device. */
  private shown: Record<DeviceKind, string> = { speaker: '', microphone: '' };
  private readonly deviceChanged = () => this.zone.run(() => { void this.refreshDevices(true); });

  constructor(readonly voice: ConversationVoiceService, private readonly zone: NgZone, private readonly dialog: MatDialog,
    private readonly keys: ApiKeysService, readonly auth: AuthService,
    private readonly confirmation: ConfirmationDialogService) {}

  get canPickSpeaker(): boolean {
    return this.voice.canSelectSpeaker && typeof (navigator.mediaDevices as OutputPicker | undefined)?.selectAudioOutput === 'function';
  }

  get ready(): boolean {
    return this.devices.every(device => this.isTested(device.kind) && this.verified[device.kind] && !this.missing(device.kind) && !this.errors[device.kind]);
  }

  ngOnInit(): void {
    this.shown = { speaker: this.selected('speaker'), microphone: this.selected('microphone') };
    navigator.mediaDevices?.addEventListener('devicechange', this.deviceChanged);
    void this.refreshDevices();
    void this.loadLive();
    this.auth.tenant.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      // A new account must not inherit the previous one's device media or in-flight writes:
      // the device preferences and test evidence are account-scoped storage, and a same-default
      // device ID would otherwise let a running test or output picker save account A's result into B.
      // The session identity is TenantContextService.sessionVersion (already bumped before this
      // fires); async continuations captured it and now see it changed. Releasing the guard lets the
      // new account start fresh — the old account's write finally is session-scoped and will not
      // touch it, and any open confirmation dialog is cancelled through its cancel signal below.
      this.stopTest();
      this.stopPreview();
      this.liveWriting = false;
      this.keyDialog?.close();
      this.sharedKeyPreviews.splice(0);
      this.selectedLiveKeyId = '';
      for (const group of this.apiKeyPreviews) { group.keys = []; group.expanded = false; group.credentialId = ''; }
      this.liveProviders[0].expanded = false;
      void this.loadLive();
    });
  }

  ngDoCheck(): void {
    for (const kind of ['speaker', 'microphone'] as const) {
      const id = this.selected(kind);
      if (id === this.shown[kind]) continue;
      this.shown[kind] = id;
      this.setTested(kind, false);
      this.errors[kind] = '';
      if (this.testing === kind) this.stopTest();
    }
  }

  toggleDevices(): void {
    this.expanded = !this.expanded;
    if (!this.expanded) this.stopTest();
  }

  apiProviderLabel(group: ApiGroupPreview, id: string): string {
    return group.providers.find(provider => provider.id === id)?.label ?? id;
  }

  keyOptions(provider: string): SharedKeyPreview[] {
    return this.sharedKeyPreviews.filter(key => key.provider === provider);
  }

  sharedKey(id: string): SharedKeyPreview | undefined {
    return this.sharedKeyPreviews.find(key => key.id === id);
  }

  selectSharedKey(id: string, model: NgModel, group?: ApiGroupPreview): void {
    if (id === 'new-key') {
      model.control.setValue(group ? group.credentialId : this.selectedLiveKeyId,
        { emitEvent: false, emitViewToModelChange: false });
      this.addSharedKey(group?.provider ?? 'openai', group);
      return;
    }
    if (group) group.credentialId = id; else this.selectedLiveKeyId = id;
  }

  addSharedKey(provider: string, group?: ApiGroupPreview): void {
    if (this.destroyed || this.keyDialog || (!group && !this.canManageLive)) return;
    const meta = this.apiKeyPreviews[0].providers.find(item => item.id === provider);
    if (!meta) return;
    const token = this.auth.getToken();
    this.keyDialog = this.dialog.open(ApiKeyCreateDialogComponent, {
      width: '460px', maxWidth: '92vw', data: group
        ? { credentialOnly: true, previewProviders: [{ ...meta, needsModel: false, modelPlaceholder: null }] }
        : { service: 'Voice', credentialOnly: true, defaultModel: this.liveProviders[0].model },
    });
    this.keyDialog.afterClosed().subscribe(result => {
      this.keyDialog = undefined;
      if (this.destroyed || token !== this.auth.getToken()) return;
      if (!group) {
        const saved: ApiKey | undefined = result?.key;
        if (!result?.saved || !saved || saved.provider !== provider) return;
        const key = { id: 'saved-' + saved.id, provider: saved.provider,
          name: saved.note || `Voice key ${saved.id}`, secretMasked: saved.secretMasked, credential: saved };
        this.sharedKeyPreviews.push(key);
        this.selectedLiveKeyId = key.id;
        if (saved.primary) this.liveCredential = saved;
        return;
      }
      if (!result?.previewKey || result.previewKey.provider !== provider) return;
      const draft: ApiKeyPreview = result.previewKey;
      const number = this.nextPreviewKeyId++;
      const key: SharedKeyPreview = { id: 'draft-' + number, provider,
        name: draft.note?.trim() || meta.label + ' key ' + number, secretMasked: draft.secretMasked };
      this.sharedKeyPreviews.push(key);
      if (group) group.credentialId = key.id; else this.selectedLiveKeyId = key.id;
    });
  }

  addApiKey(group: ApiGroupPreview): void {
    if (this.destroyed) return;
    group.expanded = true;
    group.credentialId ||= this.keyOptions(group.provider)[0]?.id ?? '';
  }

  changeApiProvider(group: ApiGroupPreview): void {
    group.credentialId = this.keyOptions(group.provider)[0]?.id ?? '';
    group.model = '';
  }

  addApiConfiguration(group: ApiGroupPreview): void {
    const key = this.sharedKey(group.credentialId);
    if (this.destroyed || !key || key.provider !== group.provider || !group.model.trim()) return;
    group.keys.push({ credentialId: key.id, provider: key.provider, model: group.model.trim(),
      secretMasked: key.secretMasked, note: null, primary: group.keys.length === 0 });
    group.expanded = false;
    group.model = '';
  }

  get selectedLiveCredential(): ApiKey | undefined {
    return this.sharedKey(this.selectedLiveKeyId)?.credential;
  }

  get canManageLive(): boolean { return this.auth.hasPermission('ApiKey.Manage'); }

  liveVoices(provider: LiveProvider): string[] { return this.providerVoices[provider.vendor] ?? []; }

  /** Each person's speaking voice for this provider, saved in this browser; an unknown saved value shows the default. */
  selectedVoice(provider: LiveProvider): string {
    const voices = this.liveVoices(provider), saved = this.voice.settings.voices[provider.vendor];
    return saved && voices.includes(saved) ? saved : voices[0] ?? '';
  }

  /** Plays the bundled sample of the selected voice through the saved speaker; a second click stops it. */
  previewing = false;
  private preview?: HTMLAudioElement;

  async previewVoice(provider: LiveProvider): Promise<void> {
    if (this.previewing) { this.stopPreview(); return; }
    const name = this.selectedVoice(provider);
    if (!name) return;
    const audio = new Audio(`assets/audio/voices/${provider.vendor}/${name}.mp3`);
    this.preview = audio; this.previewing = true;
    // Still the live preview: a Stop, panel destruction or account change replaces this.preview.
    const current = () => this.preview === audio && !this.destroyed;
    audio.onended = () => this.zone.run(() => { if (this.preview === audio) this.stopPreview(); });
    try {
      // Route through the shared speaker selection: it never silently falls back to the default
      // output for a chosen speaker, and it is the same routing a live call uses.
      await this.voice.setSpeaker(audio, this.voice.settings.speakerId);
      if (!current()) return;
      await audio.play();
      // A Stop/destroy/account change during routing or play start wins: release late playback.
      if (!current()) audio.pause();
    } catch {
      if (current()) { this.stopPreview(); this.liveError = 'Could not play the voice sample.'; }
    }
  }

  private stopPreview(): void {
    const audio = this.preview;
    this.preview = undefined; this.previewing = false;
    if (audio) { audio.onended = null; audio.pause(); audio.removeAttribute('src'); audio.load(); }
  }

  selectVoice(provider: LiveProvider, name: string): void {
    this.stopPreview();
    const voices = { ...this.voice.settings.voices, [provider.vendor]: name };
    if (!this.liveVoices(provider).includes(name) || !this.voice.saveSettings({ voices })) this.liveError = 'Could not save the voice. Please retry.';
  }

  /** The product's saved Voice key row: the Voice Primary when it is this product's, else its first. */
  liveRow(provider: LiveProvider): ApiKey | undefined {
    if (!provider.available) return undefined;
    const rows = this.companyKeys.filter(key => key.service === 'Voice' && key.provider === provider.vendor);
    return rows.find(key => key.primary) ?? rows[0];
  }

  /** The dot: Primary is the Voice key calls use (one per company). */
  isLivePrimary(provider: LiveProvider): boolean { return !!this.liveRow(provider)?.primary; }

  canSelectLivePrimary(provider: LiveProvider): boolean {
    return this.liveState(provider) === 'configured' && !this.isLivePrimary(provider) && this.canManageLive && !this.liveWriting;
  }

  /** Configured means the company has a saved Voice key (its own secret or a referenced company key). */
  liveState(provider: LiveProvider): 'disconnected' | 'configured' {
    return this.liveAvailable && this.liveRow(provider) ? 'configured' : 'disconnected';
  }

  async loadLive(refresh = false): Promise<void> {
    const version = ++this.liveVersion, token = this.auth.getToken();
    const current = () => !this.destroyed && version === this.liveVersion && token === this.auth.getToken();
    this.liveCredential = undefined;
    this.liveAvailable = false;
    this.liveLoading = true;
    this.liveError = '';
    try {
      const catalog = await firstValueFrom(this.keys.loadCatalog(refresh));
      if (!current()) return;
      // Implemented providers take their models from the backend catalog (the first is the default).
      for (const provider of this.liveProviders.filter(item => item.available))
        provider.models = catalog.providers.find(p => p.service === 'Voice' && p.id === provider.vendor)?.liveModels ?? [];
      this.providerVoices = Object.fromEntries(catalog.providers
        .filter(p => p.service === 'Voice' && p.voices?.length).map(p => [p.id, p.voices!]));
      if (!catalog.providers.some(p => p.service === 'Voice' && p.id === 'openai')) {
        this.liveError = 'GPT-Live is not enabled on this server. Please retry after the server update.';
        return;
      }
      const credentials: ApiKey[] = [];
      for (let page = 1; ; page++) {
        const result = await firstValueFrom(this.keys.list(undefined, page, 100));
        if (!current()) return;
        credentials.push(...result.items);
        if (!result.items.length || credentials.length >= result.total) break;
      }
      this.companyKeys = credentials;
      // A key that itself uses another key is not offered again; choose its source instead.
      const owned = credentials.filter(key => !key.sourceKeyId);
      const voiceCredentials = owned.filter(k => k.service === 'Voice' && k.provider === 'openai');
      this.liveCredential = voiceCredentials.find(k => k.primary) ?? voiceCredentials[0];
      const drafts = this.sharedKeyPreviews.filter(k => k.id.startsWith('draft-'));
      this.sharedKeyPreviews.splice(0, this.sharedKeyPreviews.length,
        ...owned.map(saved => ({ id: 'saved-' + saved.id, provider: saved.provider,
          name: saved.note?.trim() || `${saved.service} key ${saved.id}`, secretMasked: saved.secretMasked, credential: saved })),
        ...drafts);
      const row = this.liveRow(this.liveProviders[0]);
      if (row) this.selectedLiveKeyId = 'saved-' + (row.sourceKeyId ?? row.id);
      if (!this.keyOptions('openai').some(k => k.id === this.selectedLiveKeyId)) {
        this.selectedLiveKeyId = this.liveCredential ? 'saved-' + this.liveCredential.id : '';
      }
      for (const group of this.apiKeyPreviews) {
        if (!this.keyOptions(group.provider).some(k => k.id === group.credentialId)) group.credentialId = '';
      }
      const models = this.liveProviders[0].models, saved = row?.model ?? this.liveCredential?.model;
      this.liveProviders[0].model = saved && models.includes(saved) ? saved : models[0] ?? '';
      this.liveAvailable = true;
    } catch {
      if (current()) this.liveError = 'Could not load company API keys. Please retry.';
    } finally { if (current()) this.liveLoading = false; }
  }

  canConnect(provider: LiveProvider): boolean {
    return !this.destroyed && !this.liveWriting && provider === this.liveProviders[0] && this.liveAvailable && !this.liveLoading
      && this.liveState(provider) === 'disconnected' && provider.models.includes(provider.model)
      && this.selectedLiveCredential?.provider === 'openai';
  }

  async setLivePrimary(provider: LiveProvider): Promise<void> {
    const row = this.liveRow(provider);
    if (this.destroyed || !row || !this.canSelectLivePrimary(provider)) return;
    await this.writeLive(() => this.keys.setPrimary(row.id), 'Could not set the Primary Live provider. Please retry.');
  }

  setApiPrimary(group: ApiGroupPreview, key: ApiGroupPreview['keys'][number]): void {
    if (this.destroyed || !this.apiKeyPreviews.includes(group) || !group.keys.includes(key)) return;
    for (const item of group.keys) item.primary = item === key;
  }

  /** Saves the company Live configuration as a Voice key row; only Composer starts a voice session. */
  async connectLive(provider: LiveProvider): Promise<void> {
    const credential = this.selectedLiveCredential;
    if (!this.canConnect(provider) || !credential || !this.auth.getToken()) return;
    this.liveError = '';
    this.stopTest();
    // A Voice key is used as it is; a Chat or Agent key is referenced, not copied.
    const saved = credential.service === 'Voice'
      ? await this.writeLive(() => this.keys.setPrimary(credential.id), 'Could not save the Live configuration. Please retry.')
      : await this.writeLive(() => this.keys.add({ service: 'Voice', provider: provider.vendor, secret: null,
          model: provider.model, note: null, sourceKeyId: credential.id }), 'Could not save the Live configuration. Please retry.');
    if (saved) provider.expanded = false;
  }

  async disconnectLive(provider: LiveProvider): Promise<void> {
    const row = this.liveRow(provider);
    if (!row || this.liveWriting || !this.canManageLive) return;
    // Own the whole confirm→write workflow with the single guard, from before the dialog opens:
    // repeated Disconnect clicks cannot stack several dialogs, and the guard is released only for
    // this session. The dialog is cancelled on a session change or destruction, and even if its
    // answer still arrives we drop it when the session moved on.
    this.liveWriting = true;
    const session = this.auth.tenant.sessionVersion;
    try {
      // A reference row keeps its shared source key; a Voice row with its own secret is deleted and
      // takes any service configurations built on it with it. Spell out which case this is so the
      // user is not surprised by a cascade or misled that a shared secret was removed.
      const owned = !row.sourceKeyId;
      const confirmed = await firstValueFrom(this.confirmation.confirm({
        title: `Disconnect ${provider.name}?`,
        message: owned
          ? `This deletes the ${provider.name} key and its secret. Any other service configurations that use this key are removed too.`
          : `This removes the ${provider.name} configuration. The shared company key it uses is not deleted.`,
        confirmLabel: 'Disconnect', danger: true,
      }, merge(this.destroyed$, this.auth.tenant.sessionChanged$)));
      if (!confirmed || this.destroyed || session !== this.auth.tenant.sessionVersion) return;
      if (await this.runLiveWrite(() => this.keys.remove(row.id), 'Could not disconnect. Please retry.', session)) provider.expanded = false;
    } finally {
      // Only release the guard this session set; a session change already reset it for the new account.
      if (session === this.auth.tenant.sessionVersion) this.liveWriting = false;
    }
  }

  /** Connect/Primary entry point: holds the single mutation guard for this session, then writes. */
  private async writeLive(write: () => Observable<unknown>, failure: string): Promise<boolean> {
    if (!this.canManageLive || this.liveWriting) return false;
    this.liveWriting = true;
    const session = this.auth.tenant.sessionVersion;
    try {
      return await this.runLiveWrite(write, failure, session);
    } finally {
      if (session === this.auth.tenant.sessionVersion) this.liveWriting = false;
    }
  }

  /** Runs one company key write for a specific session, then reloads the keys (Primary may move).
   *  The caller already holds the mutation guard. A completion for a superseded session never
   *  reloads into, or reports an error on, the new account. */
  private async runLiveWrite(write: () => Observable<unknown>, failure: string, session: number): Promise<boolean> {
    try {
      await firstValueFrom(write());
      if (this.destroyed || session !== this.auth.tenant.sessionVersion) return false;
      await this.loadLive();
      return true;
    } catch {
      if (!this.destroyed && session === this.auth.tenant.sessionVersion) this.liveError = failure;
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPreview();
    ++this.liveVersion;
    this.destroyed$.next(); this.destroyed$.complete();
    this.keyDialog?.close();
    this.stopTest();
    navigator.mediaDevices?.removeEventListener('devicechange', this.deviceChanged);
  }

  // Device-list refreshes must not replace a native option while its choice is being committed.
  trackDeviceOption(_: number, option: DeviceOption): string { return option.id; }

  /** Every panel shows, tests and saves the one preference the next voice call uses. */
  selected(kind: DeviceKind): string {
    const settings = this.voice.settings;
    return kind === 'speaker' ? settings.speakerId : settings.microphoneId;
  }

  missing(kind: DeviceKind): boolean {
    const id = this.selected(kind);
    return !!id && !this.devices.find(device => device.kind === kind)!.options.some(option => option.id === id);
  }

  missingLabel(kind: DeviceKind): string {
    return this.verified[kind] ? 'Saved device (not listed)' : 'Saved device (not verified)';
  }

  async selectDevice(kind: DeviceKind, id: string, model?: NgModel): Promise<boolean> {
    if (this.testing === kind) this.stopTest();
    this.errors[kind] = '';
    // An account switch during the output picker must not save the picked device into the new account.
    const generation = this.auth.tenant.sessionVersion;
    const live = () => !this.destroyed && generation === this.auth.tenant.sessionVersion;
    if (id === '__choose__') {
      this.showSaved(kind, model);
      try {
        const device = await (navigator.mediaDevices as OutputPicker).selectAudioOutput!();
        if (!live()) return false;
        id = device.deviceId;
        const options = this.devices[0].options;
        if (!options.some(option => option.id === id)) options.push({ id, label: device.label || 'Speaker' });
      } catch (error) { if (live()) this.errors[kind] = this.errorMessage(error, kind); return false; }
    }
    if (!live()) return false;
    const change = kind === 'speaker' ? { speakerId: id } : { microphoneId: id };
    if (this.voice.saveSettings(change)) {
      this.setTested(kind, false);
      this.shown[kind] = id;
      return true;
    }
    // Keep showing the saved device: it is the one tests and the next call use.
    this.showSaved(kind, model);
    this.errors[kind] = 'Could not save device. Retry.';
    return false;
  }

  /**
   * Returns a rejected choice to the saved ID through the forms control. The select value accessor
   * keeps the rejected value and rewrites it whenever an option is added or removed, and NgModel
   * ignores a later [ngModel] equal to its view model, so both are reset without re-emitting a change.
   */
  private showSaved(kind: DeviceKind, model?: NgModel): void {
    if (!model) return;
    const id = this.selected(kind);
    model.viewModel = id;
    model.control.setValue(id, { emitViewToModelChange: false });
  }

  async test(kind: DeviceKind): Promise<void> {
    if (this.testing === kind) { this.stopTest(); return; }
    this.stopTest();
    this.errors[kind] = '';
    const id = this.selected(kind); this.shown[kind] = id;
    this.setTested(kind, false);
    this.testing = kind;
    const abort = new AbortController(); this.testAbort = abort;
    // A device ID that is the same default across accounts must not let account A's test result
    // (or its late callbacks) land as evidence in account B; tie the test to this session too.
    const generation = this.auth.tenant.sessionVersion;
    const current = () => !this.destroyed && generation === this.auth.tenant.sessionVersion && this.testAbort === abort && !abort.signal.aborted && this.selected(kind) === id;
    const failed = (message: string) => { if (current()) { this.setTested(kind, false); this.stopTest(); this.errors[kind] = message; } };
    try {
      if (kind === 'speaker') {
        await this.voice.testSpeaker(id, abort.signal, failed);
        if (current()) this.setTested('speaker', true);
      } else await this.voice.testMicrophone(id, abort.signal, value => {
        if (!current()) return;
        // Sub-segment noise stays unlit; only a visible signal passes the microphone test.
        this.inputLevel = Math.floor(value * this.segments.length);
        if (this.inputLevel > 0) this.setTested('microphone', true);
      }, failed);
      if (!current()) return;
      await this.refreshDevices();
    } catch (error) { failed(this.errorMessage(error, kind)); }
  }

  private stopTest(): void {
    const abort = this.testAbort; this.testAbort = undefined;
    abort?.abort();
    this.testing = null; this.inputLevel = 0;
  }

  private async refreshDevices(changed = false): Promise<void> {
    const version = ++this.refreshVersion;
    if (changed) this.clearTested();
    if (!navigator.mediaDevices?.enumerateDevices) {
      this.clearTested();
      this.errors.microphone = 'Device access requires a supported browser on HTTPS.';
      return;
    }
    try {
      const available = await navigator.mediaDevices.enumerateDevices();
      if (this.destroyed || version !== this.refreshVersion) return;
      if (this.errors.microphone === listError) this.errors.microphone = '';
      for (const device of this.devices) {
        const kind = device.kind === 'speaker' ? 'audiooutput' : 'audioinput';
        const listed = available.filter(item => item.kind === kind);
        const selected = this.selected(device.kind);
        const previouslyAvailable = !!selected && device.options.some(option => option.id === selected);
        // Without permission browsers blank IDs and labels or omit devices, so an empty list proves nothing.
        this.verified[device.kind] = listed.length > 0 && listed.every(item => item.deviceId && item.label);
        device.options = [{ id: '', label: 'System default' }, ...listed
          .filter(item => item.deviceId && item.deviceId !== 'default')
          .filter(() => device.kind !== 'speaker' || this.voice.canSelectSpeaker)
          .map((item, index) => ({ id: item.deviceId, label: item.label || `${device.label} ${index + 1}` }))];
        if (!this.verified[device.kind] || this.missing(device.kind)) this.setTested(device.kind, false);
        // A listed device vanished: unplugged or no longer visible. Stop using it either way, without claiming which.
        if (changed && previouslyAvailable && this.missing(device.kind)) {
          if (this.testing === device.kind) this.stopTest();
          this.errors[device.kind] = 'Device not listed. Check its connection and browser permission.';
        }
      }
    } catch {
      if (this.destroyed || version !== this.refreshVersion) return;
      // The previous list may be stale; saved IDs stay selected but unverified.
      this.clearTested();
      this.verified = { speaker: false, microphone: false };
      this.errors.microphone = listError;
    }
  }

  private errorMessage(error: unknown, kind: DeviceKind): string {
    const name = error instanceof DOMException ? error.name : '';
    if (kind === 'speaker' && !this.verified.speaker && ['NotAllowedError', 'NotFoundError'].includes(name))
      return 'Test the microphone to allow access, then retry the speaker.';
    if (name === 'NotAllowedError' || name === 'SecurityError')
      return `Allow ${kind} access in browser site settings.`;
    if (name === 'NotFoundError' || name === 'OverconstrainedError')
      return `${kind} unavailable. Choose another device.`;
    if (name === 'NotReadableError') return `Cannot open ${kind}. Check other audio apps.`;
    return error instanceof Error ? error.message : `Could not test the ${kind}. Try again.`;
  }
}
